import { UserType } from './user.model';

export enum EAccountStatus {
  OK = 'ok',
  WARNING = 'warning',
  DANGER = 'danger',
}

export interface IVisibleUsers {
  email: string;
  first_name: string;
  last_name: string;
  id: number;
  account_role: UserType;
  vsm_group_id?: number | null;
  group_status?: boolean;
  status?: EAccountStatus;
  inherit_from_id?: number | null;
  /** Admin-only: Self | Manager */
  paid_by?: string | null;
}

export enum EAccountReportStatus {
  DISABLED,
  CONTACTS_ONLY,
  CONTACTS_AND_CLOSER,
  CLOSER_ONLY
}

export enum BillingDesignation {
  STRIPE = 'stripe',
  QUICKBOOKS = 'quickbooks',
  NO_BILLING = 'no_billing',
}

export type BillingDesignationValue = BillingDesignation | null;

/** Map of designation value => display label from API extra_data.billing_designation_labels */
export type BillingDesignationLabels = Partial<Record<BillingDesignation | string, string>>;

export function isOffAppBilled(designation?: BillingDesignationValue | string | null): boolean {
  return designation === BillingDesignation.QUICKBOOKS || designation === BillingDesignation.NO_BILLING;
}

export function billingDesignationLabel(
  designation: string | null | undefined,
  labels?: BillingDesignationLabels | null,
): string {
  if (!designation) {
    return '';
  }
  return labels?.[designation] || designation;
}

export const ACCOUNT_REPORT_TYPES = {
  [EAccountReportStatus.CONTACTS_ONLY]: 'contacts',
  [EAccountReportStatus.CONTACTS_AND_CLOSER]: 'users',
  [EAccountReportStatus.CLOSER_ONLY]: 'users',
};

export interface IAccount {
  account_id: number;
  account_role: UserType;
  report_status?: EAccountReportStatus;
  actions: string[];
  contact_record: number;
  created_at: string;
  email: string;
  first_name: string;
  forward_email_enabled: boolean;
  last_name: string;
  last_signin: string;
  manager_id: number;
  notification_email: string;
  notification_email_enabled: boolean;
  partner_id: number;
  franchisee_id?: number;
  referral_partner_id?: number | null;
  referral_partner_name?: string | null;
  inherit_from_id?: number | null;
  c2recruit?: boolean;
  last_contacts_upload_at?: string;
  status: EAccountStatus;
  unknown_messages: number;
  unread_messages: number;
  unread_old_messages: number;
  vsm_id: number;
  visible_users: IVisibleUsers[];
  check_user_email_domain?: boolean;

  confirmed_at?: string;
  vsm_group_id?: number;
  group_status?: boolean;
  invoiced?: boolean;
  billing_designation?: BillingDesignationValue;
  invoice_note?: string | null;
  legacy_stripe?: boolean;
  forward_active?: number;
  forward_to?: string;
}

export interface IAccountUpdateResult {
  account_id: number;
}

export interface IAccountReportUsersColumns {
  Category: string;
  address: string;
  bounced: string;
  city: string;
  company: string;
  current_pipeline_step: string;
  current_product: number;
  current_status: string;
  date_added_to_retreva: string;
  email: string;
  email_position: number;
  first_name: string;
  industry1: string;
  last_name: string;
  next_email_at: string;
  phone: number;
  private_note: string;
  source: string;
  state: string[];
  status: string;
  tags: string;
  title: string;
  user_contact_id: number;
  user_id: number;
  variable_1: string;
  variable_2: string;
  variable_3: string;
  vip: string;
  won_amount: string;
  zip: string;
}

export interface IAccountReportCloserColumns {
  account_created_at: string;
  /** Present when the closer report is requested with with_deleted */
  account_deleted_on?: string | null;
  account_id: number;
  account_note: string;
  active_contacts: number;
  amount_gained: string;
  city: string;
  company: string;
  contacts_added_in_last_year: number;
  contacts_added_last_30_days: number;
  contacts_added_last_90_days: number;
  contacts_in_service: number;
  country: string;
  current_meetings: number;
  current_orders: number;
  current_quotes: number;
  cycle: number;
  /** Franchisee mode only: payment_discounts name with updated_at date, e.g. `CODE (2026-03-01)` */
  discount_code?: string;
  email: string;
  first_name: string;
  follow: number;
  /** Franchisee mode only: `{flat} ({pct}%)` from the last charge, e.g. `45.00 (20%)`; empty when never charged */
  franchisee_share?: string;
  identifier_number: string;
  inactive: number;
  integration_contacts_added_last_30_days: number;
  integration_data_sync_on: string;
  integration_total_contacts_added: number;
  invoice_note: string;
  /** Franchisee mode only: `YYYY-MM-DD` of the newest revenue ledger charge */
  last_charge_date?: string | null;
  /** Franchisee mode only: total amount of the newest revenue ledger charge */
  last_charge_amount?: string | null;
  last_name: string;
  lost: number;
  manager_email: string;
  opportunities: number;
  optional_note_a: string;
  optional_note_b: string;
  other: number;
  partner_email: string;
  /** Franchisee mode only: `YYYY-MM-DD` of the second newest revenue ledger charge */
  prior_charge_date?: string | null;
  /** Franchisee mode only: total amount of the second newest revenue ledger charge */
  prior_charge_amount?: string | null;
  red_no_matches: number;
  /** Franchisee mode only: referral partner share from the last charge, same format as `franchisee_share` */
  ref_share?: string;
  remove: number;
  retreva_service: string;
  service_flag: string;
  spam: number;
  state: string;
  /** Franchisee mode only: subscribed, never charged and still inside the plan trial window */
  trial?: boolean;
  unread_visible_messages: number;
  visible_messages: number;
  vsm_messages: number;
  vsm_released: number;
  won: number;
  zip: string;
}
